import { dayShort } from './types';

/**
 * Stanowiska w obsadzie (owner 2026-10-25 16:12–16:45): the places per day and function, as the server sends them
 * (`GET /api/crew/events/:id/positions`); a manual number („+ Stanowisko”) or the rules' one.
 */
export type Place = { id?: string; version?: number; place: number; label: string; description?: string; assignmentId?: string; personId?: string; personName?: string; filled?: boolean };
export type PlaceGroup = {
  functionId: string;
  functionName: string;
  others: boolean;
  source: 'manual' | 'rule';
  count: number;
  /** the rules' number: a way back from a manual one */
  ruleCount?: number;
  places: Place[];
  /** Owner 2026-10-26: the crew of the function that day in no place (beyond the number, „Odmowa”, „Niedostępny”). */
  extra?: Place[];
};
export type PositionsView = { multiDay: boolean; days: Array<{ day: string; groups: PlaceGroup[] }> };

/** „+ Stanowisko”: the select's value of „Inni” (the API takes `functionId: null`). */
export const OTHERS = '__inni__';

/** What the server answers to „Technik × N”. */
export type CountResult = { changed: boolean; released: Array<{ day: string; personId?: string; personName?: string }> };
/** What the server answers to filling a place: `otherDays` — ask „Obsadzić tę osobę także w pozostałych dniach?”. */
export type FillResult = { positionId: string; assignmentId: string; day: string; otherDays: string[] };
export type OtherDaysResult = { filled: string[]; refused: Array<{ day: string; code: string; message: string }>; collisions: Array<{ day: string; events: Array<{ eventId?: string; eventName: string }> }> };

/** What the server answers to „Dodaj” of the add row (one place; with a person: their fill). */
export type AddPlaceResult = { positionIds: string[]; fill?: FillResult; collisions: OtherDaysResult['collisions'] };
/** „Zmień osobę”: the new person's fill (`positionId`/`day` when they took a place) and the collisions it made. */
export type ChangeResult = { assignmentId: string; positionId?: string; day?: string; otherDays: string[]; collisions: OtherDaysResult['collisions'] };

/**
 * Owner 2026-10-26 („ⓘ Info” of Obsada): the number of places per function the global ⚙ shows, in the global order —
 * per day the manual number, else the rules' („z reguły” until a number is typed); `mixed`: the days differ.
 */
export type InfoSource = 'manual' | 'rule' | 'none';
export type InfoFunction = { functionId: string; functionName: string; others: boolean; count: number; source: InfoSource; ruleCount: number; mixed: boolean; perDay: Array<{ day: string; count: number; source: InfoSource }> };
export type PositionInfo = { multiDay: boolean; days: string[]; functions: InfoFunction[] };

/** The number a function's field shows for „Wszystkie dni” (`day` null) or one day; null — the days differ. */
export function infoValue(f: InfoFunction, day: string | null): { count: number | null; source: InfoSource } {
  if (day === null) return { count: f.mixed ? null : f.count, source: f.source };
  const d = f.perDay.find((x) => x.day === day);
  return d ? { count: d.count, source: d.source } : { count: f.count, source: f.source };
}

/** Owner 21:55: who may take a place — a function place only people with that function; „Inni”: anybody. */
export const mayTake = (person: { functions?: Array<{ functionId: string }> }, group: Pick<PlaceGroup, 'others' | 'functionId'>): boolean =>
  group.others || (person.functions ?? []).some((f) => f.functionId === group.functionId);

/** A place's accessible name: its label, on a multi-day event with its day first („7.11 · Technik — miejsce 1”). */
export const placeName = (day: string, label: string, multiDay: boolean) => (multiDay ? `${dayShort(day)} · ${label}` : label);

/** Who fills a place: the name, „obsadzone” when the role may not see names, else „wolne”. */
export const placeWho = (p: Place) => p.personName ?? (p.filled ? 'obsadzone' : 'wolne');
export const isFilled = (p: Place) => !!(p.personName || p.filled || p.assignmentId);

/**
 * Owner 2026-10-27 08:16 (the grid's side panel ⓘ Info — the counts left the bar): a function's filled places against
 * its places on `day`, or (null) on its worst day (the most free places); null when it has no places.
 */
export function filledOf(view: PositionsView, functionId: string, day: string | null): { filled: number; needed: number } | null {
  const counts = view.days
    .filter((d) => day === null || d.day === day)
    .flatMap((d) => d.groups.filter((g) => g.functionId === functionId && g.places.length > 0))
    .map((g) => ({ filled: g.places.filter(isFilled).length, needed: g.places.length }));
  if (counts.length === 0) return null;
  return counts.sort((a, b) => b.needed - b.filled - (a.needed - a.filled))[0]!;
}

/** The count beside a function in the side panel's Info (owner 2026-10-27 21:01): just „1/2”. */
export function filledText(c: { filled: number; needed: number }): string {
  return `${c.filled}/${c.needed}`;
}

/** „Zwolniono: Jan (7.11), Ewa (7.11)” after a lower number. */
export function releasedText(released: CountResult['released']): string | null {
  if (released.length === 0) return null;
  return `Zwolniono: ${released.map((r) => `${r.personName ?? 'osoba'} (${dayShort(r.day)})`).join(', ')}.`;
}

/** The outcome of „Tak” to the other days, in a sentence or two. */
export function otherDaysText(r: OtherDaysResult): string {
  const parts: string[] = [];
  if (r.filled.length > 0) parts.push(`Obsadzono także: ${r.filled.map(dayShort).join(', ')}.`);
  for (const x of r.refused) parts.push(x.message);
  for (const c of r.collisions) parts.push(`Kolizja ${dayShort(c.day)}: także „${c.events.map((e) => e.eventName).join('”, „')}”.`);
  return parts.length > 0 ? parts.join(' ') : 'Nic do obsadzenia w pozostałych dniach.';
}

// ---------- the quote (never synced; the screen asks) ----------

export type FunctionCompare = {
  functionId: string;
  functionName: string;
  serviceId: string | null;
  serviceName: string;
  positions: number;
  quoted: number;
  proposal: 'add' | 'remove' | null;
  /** Owner 2026-10-31 (review I1, per day): some day has fewer places than the quote — „Uzupełnij obsadę” would raise it. */
  fillable?: boolean;
};
export type PositionsCompare = {
  quote: { id: string } | null;
  heldByOther?: boolean;
  versionId?: string;
  versionNumber?: number;
  versionState?: string;
  newVersion?: boolean;
  accepted?: boolean;
  signature?: string;
  dismissed?: boolean;
  mayApply?: boolean;
  /** Owner 2026-10-31: the user may change the places in Obsada („Uzupełnij obsadę”). */
  mayFillCrew?: boolean;
  closed?: boolean;
  /** Re-review 6: functions left out (a custom line the person may not write), with why. */
  skipped?: Array<{ functionId: string; functionName: string; reason: string }>;
  functions?: FunctionCompare[];
};

/** The differences of a comparison: „Technik +1”, „Realizator −1” (owner 18:30, 2: one question per event). */
export function differences(c: PositionsCompare): Array<{ fn: FunctionCompare; delta: number; text: string }> {
  return (c.functions ?? [])
    .filter((f) => f.proposal)
    .map((f) => {
      const delta = f.positions - f.quoted;
      return { fn: f, delta, text: `${f.functionName} ${delta > 0 ? '+' : '−'}${Math.abs(delta)}` };
    });
}

/**
 * „Uzupełnij obsadę” (owner 2026-10-31, two-way; review I1: per day): the functions with some day below the quote's
 * quantity (`fillable`, the server's per-day check) — also one whose busiest day is above the quote („Dodać…”). None
 * for whoever may not change the places, a cancelled event or no quote.
 */
export function crewShort(c: PositionsCompare): FunctionCompare[] {
  if (!c.quote || !c.mayFillCrew || c.closed) return [];
  return (c.functions ?? []).filter((f) => f.fillable);
}

/** The question after a crew change: none when equal, without a quote, for whoever may not change it (18:09), or after „Nie” to the same difference (18:30, 3). */
export type QuotePrompt = { title: string; text: string; note?: string; confirm: string; newVersion: boolean; signature: string };

export function quotePrompt(c: PositionsCompare): QuotePrompt | null {
  if (!c.quote || !c.versionId || !c.mayApply || c.dismissed) return null;
  const diff = differences(c);
  if (diff.length === 0) return null;
  const adds = diff.every((d) => d.delta > 0);
  const removes = diff.every((d) => d.delta < 0);
  const ask = adds ? 'Dodać do kosztorysu?' : removes ? 'Zdjąć z kosztorysu?' : 'Zaktualizować kosztorys?';
  const text = `${diff.map((d) => `${d.text} (obsada ${d.fn.positions}, kosztorys ${d.fn.quoted})`).join(', ')} — ${ask.charAt(0).toLowerCase()}${ask.slice(1)}`;
  const notes = [c.accepted ? 'Uwaga: kosztorys jest zaakceptowany przez klienta.' : '', ...(c.skipped ?? []).map((x) => `${x.functionName}: ${x.reason}`)].filter(Boolean);
  const note = notes.length > 0 ? notes.join(' ') : undefined;
  const confirm = adds ? 'Dodaj do kosztorysu' : removes ? 'Zdejmij z kosztorysu' : 'Zaktualizuj kosztorys';
  if (c.newVersion) {
    return { title: 'Zrobić nową wersję?', text: `${text} Kosztorys (wersja ${c.versionNumber}) jest już wysłany — zmiana wymaga nowej wersji.`, ...(note ? { note } : {}), confirm: `Nowa wersja: ${confirm.toLowerCase()}`, newVersion: true, signature: c.signature ?? '' };
  }
  return { title: ask, text, ...(note ? { note } : {}), confirm, newVersion: false, signature: c.signature ?? '' };
}
